import { useState } from "react";
import {
  includes,
  initial,
  map,
  reduce,
  reject,
  split,
  startsWith,
  uniq,
} from "lodash";
import { PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause, InnerClause } from "@radarly/models/clause";
import { getFieldFromInnerClause } from "@radarly/models/clause/getter";
import {
  isClauseEmpty,
  isCustomFieldsClause,
  isNotClause,
  isTermClause,
} from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";
import { ClassificationFilter, Status } from "@radarly/models/filters";
import { useFilterbarClassificationChoices } from "./useFilterbarClassificationChoices";
import { isIncludedFromFacet } from "./utils";

const MAX_CLASSIFICATION_LEVELS = 3;

function useClassificationLevel() {
  const [level, setLevel] = useState(1);
  const [currentFilter, setCurrentFilter] = useState("");

  const onChangeLevel = (level: number, filterId: string) => {
    if (level + 1 > MAX_CLASSIFICATION_LEVELS) return null;
    setLevel(level + 1);
    setCurrentFilter(filterId);
  };

  return {
    level,
    onChangeLevel,
    currentFilter,
  };
}

function filterClassificationValues(
  value: ClassificationFilter,
  clauseValues: string[],
  status: Status
) {
  if (status !== Status.None) {
    function getRootLevels(initialLevels: string[]) {
      let currentLevel = "";
      return map(initialLevels, (initialLevel, idx) => {
        if (idx > 0) {
          currentLevel += "/";
        }
        currentLevel += initialLevel;
        return currentLevel;
      });
    }
    const levels = split(value.id, "/");
    const rootLevels = getRootLevels(initial(levels));
    return reject(clauseValues, (clauseValue) => {
      return (
        includes(rootLevels, clauseValue) ||
        startsWith(clauseValue, value.id + "/")
      );
    });
  }
  return clauseValues;
}

function getTermValuesFromClause(clause: Clause): Array<string | number> {
  function getTermValuesFromInnerClause(
    innerClause: InnerClause
  ): Array<string | number> {
    if (isTermClause(innerClause)) {
      return innerClause[getFieldFromInnerClause(innerClause)];
    } else if (isCustomFieldsClause(innerClause)) {
      return getTermValuesFromInnerClause(innerClause.customFields);
    } else if (isNotClause(innerClause)) {
      return getTermValuesFromInnerClause(innerClause.not);
    } else {
      return [];
    }
  }

  if (clause && clause.and) {
    return reduce(
      clause.and,
      (acc, innerClause) => {
        return [...acc, ...getTermValuesFromInnerClause(innerClause)];
      },
      []
    );
  } else {
    return [];
  }
}

export function getNextClassificationClause(
  clause: Clause,
  facet: FullFacet,
  value: ClassificationFilter,
  status: Status
) {
  const includedValues = [];
  const excludedValues = [];
  const clauseValues = uniq([...getTermValuesFromClause(clause), value.id]);
  const validValues = filterClassificationValues(
    value,
    clauseValues as string[],
    status
  );
  validValues.forEach((validValue) => {
    if (value.id !== validValue) {
      if (isIncludedFromFacet(clause, facet, validValue)) {
        return includedValues.push(validValue);
      }
      return excludedValues.push(validValue);
    }
    if (status === Status.Include) {
      includedValues.push(validValue);
    } else if (status === Status.Exclude) {
      excludedValues.push(validValue);
    }
  });
  return {
    and: [
      { [Facet.ContentClassification]: includedValues },
      { not: { [Facet.ContentClassification]: excludedValues } },
    ],
  };
}

export function useFilterbarClassificationPanel(
  clause: Clause,
  facet: FullFacet,
  period: PeriodRange,
  setClause: (clause: Clause) => void
) {
  const locale = useCurrentUserLocale();
  const { level, currentFilter, onChangeLevel } = useClassificationLevel();
  const { choices, levelChoices, loading, error } =
    useFilterbarClassificationChoices(clause, facet, period);

  const onChangeStatus = (
    filter: ClassificationFilter,
    status: Status,
    level?: number
  ) => {
    if (level) {
      onChangeLevel(level, filter.id);
    }
    setClause(getNextClassificationClause(clause, facet, filter, status));
  };

  const onReset = () => {
    setClause(
      refineClauseWithInnerClause(clause, {
        [Facet.ContentClassification]: [],
      })
    );
  };

  return {
    size: "l" as const,
    locale: locale,
    onReset,
    onChangeStatus,
    choices,
    levelChoices,
    loading,
    error,
    level,
    currentFilter,
    onChangeLevel,
    title: tr(locale, "FILTERBAR::PANEL::FILTERBY", {
      filter: facet.label,
    }),
    isEmpty: isClauseEmpty(clause),
  };
}
